import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { isBusinessAccount, isB2BAccount, isCollectorAccount } from '@/features/b2b/types/b2b.types';
import { ROUTES } from './routes.config';
import type { UserRole } from '@/shared/types';


interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

/**
 * Protected route component that checks authentication and role-based access
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user, isLoading, logout } = useAuthStore();

  // Show loading while checking auth state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // If authenticated but user is missing, the state is corrupted - logout and redirect
  if (!user) {
    logout();
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // Note: we no longer check for token expiry here. The refreshToken now lives
  // in an httpOnly cookie — JS can't inspect it. Expired accessTokens are handled
  // transparently by the Axios interceptor (401 → cookie-based refresh → retry).

  // Check role-based access if roles are specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect to unified home (Send/Deliver tabs)
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

/**
 * Public route that redirects authenticated users to their dashboard
 */
interface PublicRouteProps {
  children: React.ReactNode;
  redirectAuthenticated?: boolean;
}

export function PublicRoute({ children, redirectAuthenticated = false }: PublicRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Redirect authenticated users to unified home (or B2B dashboard for business/collector accounts)
  if (redirectAuthenticated && isAuthenticated && user) {
    let target = ROUTES.DASHBOARD;
    if (isCollectorAccount(user.accountType)) {
      target = ROUTES.B2B.COLLECTOR;
    } else if (isBusinessAccount(user.accountType)) {
      target = ROUTES.B2B.DASHBOARD;
    }
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}

interface KycProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard for Jetpacker-only pages.
 * Requires authentication AND KYC verification (isKycVerified).
 * Unverified users are redirected to KYC education page.
 */
export function KycProtectedRoute({ children }: KycProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, logout } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  if (!user) {
    logout();
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  // Redirect unverified users to KYC education.
  // Trust the JWT role (set by backend after KYC), not the local isKycVerified flag.
  if (user.role !== 'JETPACKER') {
    return <Navigate to={ROUTES.AUTH.KYC_EDUCATION} replace />;
  }

  return <>{children}</>;
}

interface B2BProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Route guard for B2B operator pages.
 * Requires authentication AND business account type.
 */
export function B2BProtectedRoute({ children }: B2BProtectedRouteProps) {
  const location = useLocation();
  const { isAuthenticated, user, isLoading, logout } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  if (!user) {
    logout();
    return <Navigate to={ROUTES.AUTH.LOGIN} state={{ from: location }} replace />;
  }

  // If not a B2B or collector account → redirect to home
  if (!isB2BAccount(user.accountType)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  // If collector → redirect to collector view (unless already on a collector route)
  if (isCollectorAccount(user.accountType)) {
    if (!location.pathname.startsWith('/b2b/collector')) {
      return <Navigate to={ROUTES.B2B.COLLECTOR} replace />;
    }
  }

  return <>{children}</>;
}
